<template>
  <div
    ref="moduleRef"
    class="video-testimonials"
  >
    <div class="video-testimonials__container">
      <div class="video-testimonials__container__header">
        <h2 class="video-testimonials__container__header--title">
          {{ t('b2b__landing_page__video_testimonials__title') }}
        </h2>
      </div>
      <div
        ref="cardsRef"
        class="video-testimonials__container__cards"
        @scroll.passive="updateActiveSlide"
      >
        <div
          v-for="(card, index) in cards"
          :key="index"
          class="video-testimonials__container__cards--card"
        >
          <video
            ref="videos"
            :src="card.src + '#t=0.01'"
            playsinline
            :aria-label="`Video testimonial by ${card.author}: ${card.title}`"
            @click="togglePlay(index)"
            @play="onPlay(index)"
            @pause="onPause(index)"
            @ended="onEnded(index)"
            @waiting="onWaiting(index)"
            @canplay="onCanPlay(index)"
          />
          <div
            v-show="currentlyPlaying === index"
            class="controls"
          >
            <BaseButton
              icon
              inverted
              class="stop-button"
              aria-label="Pause video"
              @click="togglePlay(index)"
            >
              <KDSSpinner v-show="isLoading" />
              <PauseIcon v-show="!isLoading" />
            </BaseButton>
          </div>
          <div
            v-show="currentlyPlaying !== index"
            class="overlay"
            @click="togglePlay(index)"
          >
            <BaseButton
              icon
              inverted
              aria-label="Play video"
              class="play-button"
            >
              <PlayIcon />
            </BaseButton>
            <p
              class="kds-typography-title-medium kds-color-text-on-interactive-strong"
            >
              {{ card.title }}
            </p>
            <p
              v-if="card.author"
              class="kds-typography-title-large kds-color-text-on-interactive-strong"
            >
              {{ card.author }}
            </p>
          </div>
        </div>
      </div>
      <div
        v-if="activeSlide < cards.length - 1"
        class="video-testimonials__container__swipe-hint"
        aria-hidden="true"
      />
      <div class="video-testimonials__container__dots">
        <button
          v-for="(_, index) in cards"
          :key="`dot-${index}`"
          type="button"
          class="video-testimonials__container__dots--dot"
          :class="{
            'video-testimonials__container__dots--dot-active':
              index === activeSlide,
          }"
          :aria-label="
            t('b2b__landing_page__video_testimonials__go_to_testimonial', {
              index: index + 1,
            })
          "
          @click="goToSlide(index)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import testimonial1 from '@us/app/assets/videos/Nora_Testimonial.mp4'
import testimonial2 from '@us/app/assets/videos/Nameless_Testimonial.mp4'
import testimonial3 from '@us/app/assets/videos/Devin_Testimonial.mp4'
import BaseButton from '@shared/components/BaseButton.vue'
import PlayIcon from '@shared/assets/icon/PlayIcon.vue'
import PauseIcon from '@shared/assets/icon/PauseIcon.vue'
import KDSSpinner from '@shared/components/KDSSpinner.vue'

const { t } = useI18n()
const moduleRef = ref(null)
const videoRefs = useTemplateRef('videos')
const cardsRef = ref(null)
const activeSlide = ref(0)
const currentlyPlaying = ref(null)
const isLoading = ref(false)

const cards = [
  {
    title: t('b2b__landing_page__video_testimonials__card_one__title'),
    author: t('b2b__landing_page__video_testimonials__card_one__author'),
    src: testimonial1,
  },
  {
    title: t('b2b__landing_page__video_testimonials__card_two__title'),
    src: testimonial2,
  },
  {
    title: t('b2b__landing_page__video_testimonials__card_three__title'),
    author: t('b2b__landing_page__video_testimonials__card_three__author'),
    src: testimonial3,
  },
]

const stopVideo = (index) => {
  const video = videoRefs.value?.[index]
  if (!video || video.paused) return
  video.pause()
  video.currentTime = 0
}

const pauseAll = () => {
  if (currentlyPlaying.value !== null) {
    stopVideo(currentlyPlaying.value)
  }
}

const onPlay = (index) => {
  currentlyPlaying.value = index
  isLoading.value = false
  activeSlide.value = index
}

const onPause = (index) => {
  if (currentlyPlaying.value === index) {
    currentlyPlaying.value = null
    isLoading.value = false
  }
}

const togglePlay = (index) => {
  if (currentlyPlaying.value === index) {
    stopVideo(index)
  } else {
    pauseAll()
    videoRefs.value?.[index]?.play()?.catch(() => {
      currentlyPlaying.value = null
      isLoading.value = false
    })
  }
}

const onEnded = (index) => {
  const video = videoRefs.value?.[index]
  if (video) video.currentTime = 0
  currentlyPlaying.value = null
  isLoading.value = false

  const next = index + 1
  if (next < cards.length) {
    videoRefs.value?.[next]?.play()?.catch(() => {})
  }
}

const onWaiting = (index) => {
  if (currentlyPlaying.value === index) isLoading.value = true
}

const onCanPlay = (index) => {
  if (currentlyPlaying.value === index) isLoading.value = false
}

const updateActiveSlide = () => {
  const container = cardsRef.value
  if (!container) return
  const cardEls = [...container.children]
  if (!cardEls.length) return

  const center = container.scrollLeft + container.clientWidth / 2
  let closest = 0
  let minDist = Infinity

  cardEls.forEach((card, i) => {
    const dist = Math.abs(card.offsetLeft + card.offsetWidth / 2 - center)
    if (dist < minDist) {
      minDist = dist
      closest = i
    }
  })

  if (closest !== activeSlide.value) {
    activeSlide.value = closest
    if (currentlyPlaying.value !== null && currentlyPlaying.value !== closest) {
      pauseAll()
    }
  }
}

const goToSlide = (index) => {
  const container = cardsRef.value
  const card = container?.children[index]
  if (!card) return

  activeSlide.value = index
  container.scrollTo({ left: card.offsetLeft, behavior: 'smooth' })
}

let observer = null

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) pauseAll()
      })
    },
    { threshold: 0, rootMargin: '0px' },
  )

  if (moduleRef.value) {
    observer.observe(moduleRef.value)
  }

  updateActiveSlide()
})

onUnmounted(() => {
  observer?.disconnect()
})
</script>

<style lang="scss" scoped>
.video-testimonials {
  padding: 40px 10px;

  @include media-up(lg) {
    padding: $kds-space-12x 10px;
  }

  &__container {
    @extend %container;
    margin: auto;
    position: relative;

    &__header {
      @include flex-container(column, center, md, center);
      @include margin(xxl, bottom);

      &--title {
        @include kds-typography-display-small;
        font-weight: $kds-typography-font-weight-medium;
        text-align: center;

        @include media-up(md) {
          @include kds-typography-display-medium;
          font-weight: $kds-typography-font-weight-medium;
        }
      }
    }

    &__cards {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }

      @include media-up(md) {
        gap: $kds-space-2x;
      }

      @include media-up(lg) {
        overflow: visible;
        scroll-snap-type: none;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
        gap: 32px;
      }

      &--card {
        position: relative;
        flex: 0 0 100%;
        scroll-snap-align: start;

        @include media-up(md) {
          flex: 0 0 calc((100% - #{$kds-space-2x}) / 2.1);
        }

        @include media-up(lg) {
          flex: unset;
          scroll-snap-align: none;
        }

        video {
          display: block;
          width: 100%;
          border-radius: 16px;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }

        .controls {
          position: absolute;
          right: 16px;
          bottom: 16px;

          .stop-button {
            transition: transform 0.2s ease;
          }
        }

        .overlay {
          cursor: pointer;
          position: absolute;
          border-radius: 16px;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: start;
          justify-content: end;
          background-color: $kds-color-icon-on-interactive-disabled;
          gap: 16px;
          padding: $kds-space-2x;
          box-sizing: border-box;
          user-select: none;
          -webkit-tap-highlight-color: transparent;

          .play-button {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            transition: transform 0.2s ease;
          }
        }

        &:hover {
          .stop-button {
            transform: scale(1.1);
          }

          .play-button {
            transform: translate(-50%, -50%) scale(1.1);
          }
        }
      }
    }

    &__dots {
      margin-top: $kds-space-2x;
      display: flex;
      justify-content: center;
      gap: 8px;

      @include media-up(lg) {
        display: none;
      }

      &--dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        border: 0;
        padding: 0;
        background: $kds-color-background-strong;
        cursor: pointer;

        &-active {
          background: $kds-color-background-interactive-accent;
        }
      }
    }

    &__swipe-hint {
      position: absolute;
      right: 10px;
      top: calc(50% + 16px);
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.88);
      box-shadow: 0 1px 4px rgba(10, 28, 64, 0.16);
      pointer-events: none;
      z-index: 2;
      animation: swipe-hint-pulse 1.8s ease-in-out infinite;

      @include media-up(lg) {
        display: none;
      }

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 8px;
        height: 8px;
        border-top: 2px solid $kds-color-text-interactive-accent;
        border-right: 2px solid $kds-color-text-interactive-accent;
        transform: translate(-60%, -50%) rotate(45deg);
      }
    }
  }

  @include media-between(md, 1023px) {
    &__container {
      &__cards {
        display: flex;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        gap: $kds-space-2x;
      }

      &__cards--card {
        flex: 0 0 calc((100% - #{$kds-space-2x}) / 2.1);
        scroll-snap-align: start;
      }

      &__dots {
        display: flex;
      }

      &__swipe-hint {
        display: block;
      }
    }
  }
}

@keyframes swipe-hint-pulse {
  0%,
  100% {
    transform: translateX(0);
    opacity: 0.65;
  }

  50% {
    transform: translateX(3px);
    opacity: 1;
  }
}
</style>
